import { Link, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Phone, ChevronDown, Check } from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import { Reveal, StaggerContainer, staggerItem } from '../components/Motion';
import { COMPANY, SERVICES } from '../data/site';

export default function ServicePage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '');
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/" replace />;

  return (
    <>
      {/* Hero */}
      <section className="hero-media relative min-h-[92vh] md:min-h-[100vh] overflow-hidden bg-black">
        <motion.div
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.2, 0.8, 0.2, 1] }}
          className="absolute inset-0"
        >
          <img src={service.hero} alt={service.title} className="w-full h-full object-cover opacity-70" style={{ filter: 'grayscale(35%) contrast(1.05)' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
          <div className="absolute inset-0 grid-bg-fine opacity-40" />
        </motion.div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 pt-40 md:pt-52 pb-16">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-9">
              <div className="flex items-center gap-4 mb-8">
                <div className="font-display italic text-6xl md:text-7xl leading-none text-white/80">{service.index}</div>
                <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/60">{service.kicker}</div>
              </div>
              <h1 className="font-display italic text-[15vw] sm:text-[12vw] lg:text-[10vw] leading-[0.9] tracking-tight">
                <span className="clip-reveal"><span>{service.title}</span></span>
              </h1>
              <p className="mt-8 max-w-2xl text-white/70 text-lg leading-relaxed">
                {service.baseline}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/contact" className="shine-wrap inline-flex items-center gap-3 bg-white text-black px-6 h-12 font-mono text-[11px] tracking-[0.22em] uppercase">Demander un devis <ArrowUpRight size={14} strokeWidth={1.5} /></Link>
                <a href={`tel:${COMPANY.phoneTel}`} className="shine-wrap inline-flex items-center gap-3 border border-white/25 px-6 h-12 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/10"><Phone size={14} strokeWidth={1.5} /> Appeler</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prestations list */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <AnimatedBackground />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <Reveal>
              <div className="overline mb-4">◍ Prestations · {service.index}</div>
              <h2 className="font-display italic text-5xl md:text-6xl leading-[0.95]">Ce que nous prenons en charge.</h2>
              <p className="mt-6 text-white/60 max-w-md leading-relaxed">
                Interventions couvertes par notre atelier — chaque projet fait l’objet
                d’une étude sur mesure adaptée à votre lieu.
              </p>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              {service.highlights.map((h, i) => (
                <motion.div
                  key={h}
                  variants={staggerItem}
                  className="flex items-start gap-3 py-4 border-b border-white/10"
                >
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40 pt-1 w-8">{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-white/90 text-base">{h}</span>
                </motion.div>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-4">
              <div className="overline mb-4">◍ Questions</div>
              <h2 className="font-display italic text-4xl md:text-5xl leading-tight">FAQ.</h2>
              <p className="mt-4 text-white/60 text-sm max-w-xs">Les questions que l’on nous pose le plus souvent.</p>
            </div>
            <div className="col-span-12 md:col-span-8">
              {service.faq.map((f, i) => (
                <details key={i} className="group py-6 border-b border-white/10">
                  <summary className="flex items-start justify-between cursor-pointer list-none">
                    <span className="font-display italic text-2xl md:text-3xl pr-6 leading-tight">{f.q}</span>
                    <ChevronDown size={20} strokeWidth={1.25} className="mt-2 transition-transform group-open:rotate-180 text-white/70" />
                  </summary>
                  <div className="mt-4 text-white/60 leading-relaxed max-w-3xl">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden">
        <AnimatedBackground />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-display italic text-5xl md:text-6xl leading-[0.95]">
              Un projet en <em className="not-italic underline decoration-white/40 underline-offset-4">{service.title.toLowerCase()}</em> ?
            </h2>
            <p className="mt-6 text-white/60 max-w-lg">
              Décrivez-nous votre besoin ou appelez-nous, nous vous répondons rapidement avec une
              proposition adaptée.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end gap-3">
            <a href={`tel:${COMPANY.phoneTel}`} className="shine-wrap inline-flex items-center gap-2 border border-white/25 px-5 h-12 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/10"><Phone size={14} strokeWidth={1.5} /> {COMPANY.phone}</a>
            <Link to="/contact" className="shine-wrap inline-flex items-center gap-2 bg-white text-black px-5 h-12 font-mono text-[11px] tracking-[0.22em] uppercase">Devis <ArrowUpRight size={14} strokeWidth={1.5} /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
